import { useState, useEffect } from 'react';
import api from '../utils/api';
import { message } from 'antd';

// Custom hook สำหรับจัดการ Product Categories
const useProductCategories = (options = {}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    includeCode = false,
    includeProductCount = false,
    autoLoad = true,
    ...requestOptions
  } = options;

  // Load categories from API
  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/products/categories/list');
      
      if (response.data.success && response.data.data) {
        let processedCategories = response.data.data;
        
        // Format categories for select options
        const formattedCategories = processedCategories.map(category => ({
          display_order: category.display_order,
          category_name: category.category_name,
          category_code: category.category_code,
          value: category.value,
          label: category.label,
          color: category.color,
          id: category.id,
          description: category.description,
          product_count: category.product_count,
          is_active: category.is_active,
          ...(includeCode && { code: category.value }),
          ...(includeProductCount && { count: category.product_count || 0 })
        }));

        setCategories(formattedCategories);
        return formattedCategories;
      } else {
        throw new Error('Failed to load categories');
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err.message);
      
      // Fallback to empty array if API fails
      setCategories([]);
      
      if (!requestOptions.suppressErrorMessage) {
        message.error('ไม่สามารถโหลดหมวดหมู่สินค้าได้');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create new category
  const createCategory = async (categoryData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/api/products/categories', categoryData);
      
      if (response.data.success) {
        message.success('เพิ่มหมวดหมู่สำเร็จ');
        // Reload categories to include the new one
        await loadCategories();
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create category');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err.message);
      
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      } else if (err.message) {
        message.error(err.message);
      } else {
        message.error('ไม่สามารถเพิ่มหมวดหมู่ได้');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update category
  const updateCategory = async (id, categoryData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.put(`/api/products/categories/${id}`, categoryData);
      
      if (response.data.success) {
        message.success('อัปเดตหมวดหมู่สำเร็จ');
        // Reload categories to reflect changes
        await loadCategories();
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update category');
      }
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err.message);
      
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      } else if (err.message) {
        message.error(err.message);
      } else {
        message.error('ไม่สามารถอัปเดตหมวดหมู่ได้');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCategoryStatus = async (id, categoryData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.put(`/api/products/categories/status/${id}`, categoryData);
      
      if (response.data.success) {
        message.success('อัปเดตหมวดหมู่สำเร็จ');
        // Reload categories to reflect changes
        await loadCategories();
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update category');
      }
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err.message);
      
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      } else if (err.message) {
        message.error(err.message);
      } else {
        message.error('ไม่สามารถอัปเดตหมวดหมู่ได้');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.delete(`/api/products/categories/${id}`);
      
      if (response.data.success) {
        message.success('ลบหมวดหมู่สำเร็จ');
        // Reload categories to reflect changes
        await loadCategories();
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete category');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.message);
      
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      } else if (err.message) {
        message.error(err.message);
      } else {
        message.error('ไม่สามารถลบหมวดหมู่ได้');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get category by ID
  const getCategoryById = async (id) => {
    try {
      const response = await api.get(`/api/products/categories/${id}`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error('Failed to load category');
      }
    } catch (err) {
      console.error('Error loading category:', err);
      throw err;
    }
  };

  // Get category by code
  const getCategoryByCode = (code) => {
    return categories.find(cat => cat.value === code);
  };

  // Get categories for select options (convenience method)
  const getSelectOptions = () => {
    return categories.map(category => ({
      value: category.value,
      label: category.label,
      color: category.color
    }));
  };

  // Get categories with product count
  const getCategoriesWithCount = () => {
    return categories.filter(category => category.count > 0);
  };

  // Initialize categories
  useEffect(() => {
    if (autoLoad) {
      loadCategories().catch(() => {
        // Error handling is done in loadCategories
      });
    }
  }, [autoLoad, includeCode, includeProductCount]);

  // Public API
  return {
    // Data
    categories,
    loading,
    error,
    
    // Methods
    loadCategories,
    createCategory,
    updateCategory,
    updateCategoryStatus,
    deleteCategory,
    getCategoryById,
    getCategoryByCode,

    
    // Convenience methods
    getSelectOptions,
    getCategoriesWithCount,
    
    // Utility methods
    isLoading: loading,
    hasError: !!error,
    isEmpty: categories.length === 0,
    hasCategories: categories.length > 0,
    
    // Refresh all data
    refresh: loadCategories
  };
};

// Named exports for different use cases
export const useProductCategoriesOptions = (options = {}) => {
  const hook = useProductCategories(options);
  
  return {
    categories: hook.getSelectOptions(),
    loading: hook.loading,
    error: hook.error,
    refresh: hook.refresh
  };
};

export const useProductCategoriesWithCount = (options = {}) => {
  const hook = useProductCategories({ ...options, includeProductCount: true });
  
  return {
    categories: hook.categories,
    loading: hook.loading,
    error: hook.error,
    categoriesWithCount: hook.getCategoriesWithCount(),
    refresh: hook.refresh
  };
};

export default useProductCategories;